import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
// Backwards-compatible alias used by student pages.
export const useApp = () => useContext(AuthContext);

const TOKEN_KEY = "cla_token";
const ADMIN_TOKEN_KEY = "cla_admin_token";

function ImpersonationBanner({ user, onExit }) {
  const label = user?.name || user?.username || user?.email || "user";
  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[70] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-[#7A3FF2] px-4 py-2 text-center text-sm font-bold text-white shadow-md"
    >
      <span>
        Viewing as <span className="underline underline-offset-2">{label}</span>
        {user?.role ? ` (${user.role})` : ""} — actions affect their account.
      </span>
      <button
        data-testid="exit-impersonation-btn"
        onClick={onExit}
        className="rounded-full bg-white/25 px-3 py-1 font-bold transition-colors hover:bg-white/40"
      >
        Return to Admin
      </button>
    </div>
  );
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState([]);
  const [overlay, setOverlay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(
    typeof window !== "undefined" && !!localStorage.getItem(ADMIN_TOKEN_KEY)
  );

  const loadProgress = useCallback(async (studentId) => {
    const data = await api.getProgress(studentId);
    setProgress(data);
    return data;
  }, []);

  const loadOverlay = useCallback(async () => {
    try {
      const data = await api.getCurriculum();
      setOverlay(data);
      return data;
    } catch (_) {
      return null;
    }
  }, []);

  const applySession = useCallback(
    async (token, userObj) => {
      setAuthToken(token);
      localStorage.setItem(TOKEN_KEY, token);
      setUser(userObj);
      if (userObj.role === "student") {
        try {
          await loadProgress(userObj.id);
          await loadOverlay();
        } catch (_) {}
      }
    },
    [loadProgress, loadOverlay]
  );

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    setAuthToken(token);
    (async () => {
      try {
        const u = await api.me();
        setUser(u);
        if (u.role === "student") { await loadProgress(u.id); await loadOverlay(); }
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadProgress, loadOverlay]);

  const login = useCallback(
    async (email, password) => {
      const { token, user: u } = await api.login({ email, password });
      await applySession(token, u);
      return u;
    },
    [applySession]
  );

  const studentLogin = useCallback(
    async (username, password, class_code) => {
      const payload = { username, password };
      if (class_code) payload.class_code = class_code;
      const { token, user: u } = await api.studentLogin(payload);
      await applySession(token, u);
      return u;
    },
    [applySession]
  );

  const register = useCallback(
    async (data) => {
      const { token, user: u } = await api.register(data);
      await applySession(token, u);
      return u;
    },
    [applySession]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setImpersonating(false);
    setAuthToken(null);
    setUser(null);
    setProgress([]);
    setOverlay(null);
  }, []);

  const impersonate = useCallback(
    async (userId) => {
      const adminToken = localStorage.getItem(TOKEN_KEY);
      const { token, user: u } = await api.impersonate(userId);
      if (adminToken) localStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
      setImpersonating(true);
      await applySession(token, u);
      window.location.href = "/";
      return u;
    },
    [applySession]
  );

  const stopImpersonating = useCallback(async () => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!adminToken) return;
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setImpersonating(false);
    setAuthToken(adminToken);
    localStorage.setItem(TOKEN_KEY, adminToken);
    window.location.href = "/";
  }, []);

  const refreshProgress = useCallback(async () => {
    if (user && user.role === "student") return loadProgress(user.id);
  }, [user, loadProgress]);

  const refreshCurriculum = useCallback(async () => {
    if (user && user.role === "student") return loadOverlay();
  }, [user, loadOverlay]);

  const refreshUser = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
      return u;
    } catch (_) {
      return null;
    }
  }, []);

  const progressFor = useCallback((letter) => progress.find((p) => p.letter === letter), [progress]);

  return (
    <AuthContext.Provider
      value={{
        user,
        role: user?.role,
        progress,
        overlay,
        loading,
        impersonating,
        impersonate,
        stopImpersonating,
        login,
        studentLogin,
        register,
        setSession: applySession,
        logout,
        refreshProgress,
        refreshCurriculum,
        refreshUser,
        progressFor,
      }}
    >
      {impersonating && user && <ImpersonationBanner user={user} onExit={stopImpersonating} />}
      {children}
    </AuthContext.Provider>
  );
}
